export type Bucket = 'today' | 'thisWeek' | 'lastWeek' | 'older';

export const BUCKET_LABELS: Record<Bucket, string> = {
  today: 'Today',
  thisWeek: 'This week',
  lastWeek: 'Last week',
  older: 'Older',
};

export const BUCKET_ORDER: Bucket[] = ['today', 'thisWeek', 'lastWeek', 'older'];

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

// Monday is the first day of the week.
function startOfWeek(d: Date): Date {
  const day = d.getDay(); // 0 = Sun, 1 = Mon
  const diff = (day + 6) % 7;
  const out = startOfDay(d);
  out.setDate(out.getDate() - diff);
  return out;
}

export function bucketFor(value: Date | string | number, now: Date = new Date()): Bucket {
  const d = value instanceof Date ? value : new Date(value);
  const today = startOfDay(now);
  if (d >= today) return 'today';

  const thisWeek = startOfWeek(now);
  if (d >= thisWeek) return 'thisWeek';

  const lastWeek = new Date(thisWeek);
  lastWeek.setDate(lastWeek.getDate() - 7);
  if (d >= lastWeek) return 'lastWeek';

  return 'older';
}

const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

export function formatRelative(value: Date | string | number, now: Date = new Date()): string {
  const d = value instanceof Date ? value : new Date(value);
  const diff = now.getTime() - d.getTime();

  if (diff < 0) return 'just now';
  if (diff < 45 * 1000) return 'just now';
  if (diff < HOUR) {
    const m = Math.max(1, Math.round(diff / MIN));
    return `${m}m ago`;
  }
  if (diff < DAY) {
    const h = Math.round(diff / HOUR);
    return `${h}h ago`;
  }
  if (diff < 2 * DAY) return 'yesterday';
  if (diff < WEEK) {
    const days = Math.round(diff / DAY);
    return `${days}d ago`;
  }
  if (diff < 4 * WEEK) {
    const w = Math.round(diff / WEEK);
    return `${w}w ago`;
  }
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatExact(value: Date | string | number): string {
  const d = value instanceof Date ? value : new Date(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatDurationSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))}s`;
  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}
